/**
 * AMNotificationBell — Bell icon with unread count badge + dropdown
 *
 * Shows recent AM notifications in a popover. Clicking a notification
 * marks it as read. "Mark all as read" button at the top.
 */

import { Bell, AlertTriangle, Clock, Heart, FileText, MessageSquare, CheckSquare } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useAMNotifications, type AMNotification, type AMNotificationType } from "@/hooks/useAMNotifications";
import { formatDistanceToNow } from "date-fns";

interface AMNotificationBellProps {
  amId: string | null;
}

const typeConfig: Record<AMNotificationType, { icon: typeof Bell; color: string }> = {
  sla_breach: { icon: AlertTriangle, color: "text-red-500" },
  sla_warning: { icon: Clock, color: "text-amber-500" },
  health_alert: { icon: Heart, color: "text-orange-500" },
  contract_expiring: { icon: FileText, color: "text-blue-500" },
  task_auto_created: { icon: CheckSquare, color: "text-purple-500" },
  new_message: { icon: MessageSquare, color: "text-green-500" },
};

function NotificationItem({
  notification,
  onRead,
}: {
  notification: AMNotification;
  onRead: (id: string) => void;
}) {
  const config = typeConfig[notification.type] || typeConfig.task_auto_created;
  const Icon = config.icon;
  const isUnread = !notification.readAt;

  return (
    <button
      className={cn(
        "w-full text-left px-3 py-2.5 flex gap-2.5 hover:bg-muted/50 transition-colors border-b last:border-0",
        isUnread && "bg-[#D4A853]/5"
      )}
      onClick={() => isUnread && onRead(notification.id)}
    >
      <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", config.color)} />
      <div className="flex-1 min-w-0">
        <p className={cn("text-sm leading-tight", isUnread ? "font-medium" : "text-muted-foreground")}>
          {notification.title}
        </p>
        {notification.body && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{notification.body}</p>
        )}
        <p className="text-[10px] text-muted-foreground/70 mt-1">
          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
        </p>
      </div>
      {isUnread && <span className="h-2 w-2 rounded-full bg-[#D4A853] mt-1.5 shrink-0" />}
    </button>
  );
}

export default function AMNotificationBell({ amId }: AMNotificationBellProps) {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useAMNotifications(amId);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative text-muted-foreground hover:text-foreground transition-colors p-1">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center px-1">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" sideOffset={8}>
        <div className="flex items-center justify-between px-3 py-2.5 border-b">
          <span className="text-sm font-semibold">Notifications</span>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={markAllAsRead}>
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {notifications.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No notifications yet
            </div>
          ) : (
            notifications.map((n) => (
              <NotificationItem key={n.id} notification={n} onRead={markAsRead} />
            ))
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
